import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useAuth } from '../../auth/AuthContext';
import Transition from '../../utils/Transition';
import ModalSearch from '../../components/ModalSearch';

function ForumRightContent({ modalOpen, setModalOpen }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedStock, setSelectedStock] = useState(null);
  const [showStockSearch, setShowStockSearch] = useState(false);
  const [followedUsers, setFollowedUsers] = useState([]);
  const [followedStocks, setFollowedStocks] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const db = getFirestore();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        // For followed users
        const followingRef = collection(db, 'following');
        const userQuery = query(followingRef, where('followerId', '==', user.uid));
        const userSnapshot = await getDocs(userQuery);
        const users = userSnapshot.docs.map(doc => doc.data());
        setFollowedUsers(users);

        // For followed stocks - update collection path
        const userStocksRef = collection(db, 'users', user.uid, 'followedStocks');
        const stockSnapshot = await getDocs(userStocksRef);
        const stocks = stockSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFollowedStocks(stocks);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [db, user]);

  const handleStockSelect = (stock) => {
    setSelectedStock({
      symbol: stock.symbol,
      name: stock.name || stock.shortName || stock.symbol
    });
    setShowStockSearch(false);
  };

  const handleDeleteStock = async (id) => {
    if (!user) return;
  
    setFollowedStocks(prev => prev.filter(s => s.id !== id));
  
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'followedStocks', id));
    } catch (error) {
      console.error('Error deleting stock:', error);
      setFollowedStocks(prev => [...prev, id]);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !selectedStock) return;
  
    try {
      setIsModalLoading(true);
      const postData = {
        title,
        content,
        authorId: user.uid,
        authorName: user.displayName,
        authorPhotoURL: user.photoURL,
        timestamp: new Date(),
        upvotes: 0,
        comments: 0,
        stockSymbol: selectedStock.symbol,
        stockName: selectedStock.name || selectedStock.symbol
      };
  
      if (!postData.stockSymbol || !postData.stockName) {
        console.error('Missing required stock data');
        return;
      }
  
      await addDoc(collection(db, 'posts'), postData);
      setModalOpen(false);
      setTitle('');
      setContent('');
      setSelectedStock(null);
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setIsModalLoading(false);
    }
  };

  return (
    <div className="w-full xl:w-72">
      <div className="lg:sticky lg:top-16 lg:h-[calc(100dvh-64px)] lg:overflow-x-hidden lg:overflow-y-auto no-scrollbar">
        <div className="py-4 md:py-8 px-4 xl:px-0">
          <div className="hidden xl:block">
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-3">
              Værdipapirer jeg følger
            </div>
            <ul className="flex flex-nowrap md:block mr-3 md:mr-0">
              {followedStocks.length > 0 ? (
                followedStocks.map((stock) => (
                  <li key={stock.id} className="mr-0.5 md:mr-0 md:mb-0.5 flex items-center justify-between">
                    <a className="flex items-center px-2.5 py-2 rounded-lg whitespace-nowrap" href="#0">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        {stock.symbol}
                      </span>
                    </a>
                    <button 
                      onClick={() => handleDeleteStock(stock.id)}
                      className="text-red-500 hover:text-red-600 p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))
              ) : (
                <li className="mr-0.5 md:mr-0 md:mb-0.5">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Ingen fulgte værdipapirer
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <Transition
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        className="fixed inset-0 z-50 bg-black bg-opacity-40"
        aria-hidden="true"
      />

      <Transition
        show={modalOpen}
        enter="transition ease-out duration-300"
        enterStart="opacity-0 scale-95"
        enterEnd="opacity-100 scale-100"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100 scale-100"
        leaveEnd="opacity-0 scale-95"
        className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6"
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-lg w-full max-h-full overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 relative">
            {isModalLoading && (
              <div className="flex items-center justify-center absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-10">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-violet-500" />
              </div>
            )}
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Opret nyt indlæg</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">
                    Vælg aktie
                  </label>
                  {selectedStock ? (
                    <div className="flex items-center justify-between p-2 border rounded dark:border-gray-600">
                      <span className="text-gray-800 dark:text-gray-200">
                        {selectedStock.symbol} - {selectedStock.name}
                      </span>
                      <button 
                        type="button"
                        onClick={() => setSelectedStock(null)}
                        className="text-red-500 hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowStockSearch(true)}
                      className="w-full text-left p-2 border rounded dark:border-gray-600 text-gray-500 hover:border-gray-400 dark:hover:border-gray-500"
                    >
                      Søg efter aktie...
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400" htmlFor="title">
                    Titel
                  </label>
                  <input
                    id="title"
                    className="form-input w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400" htmlFor="content">
                    Indhold
                  </label>
                  <textarea
                    id="content"
                    className="form-textarea w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                    rows="4"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={() => {
                      setModalOpen(false);
                      setSelectedStock(null);
                    }}
                    className="btn-sm border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300"
                  >
                    Annuller
                  </button>
                  <button 
                    type="submit"
                    disabled={!selectedStock}
                    className="btn-sm bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-50"
                  >
                    Opret indlæg
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Transition>

      <ModalSearch
        id="stock-search"
        modalOpen={showStockSearch}
        setModalOpen={setShowStockSearch}
        onStockSelect={handleStockSelect}
      />
    </div>
  );
}

ForumRightContent.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired
};

export default ForumRightContent;